/**
 * In-memory OPS session state (no HTTP deps — safe for Auth logout).
 */

type MemoryCredential = {
  token: string;
  expiresAtMs: number;
  /** `${companyId}:${userId}` — never reuse across identities. */
  identityKey: string;
  /** Space-separated scopes from mint response (never trust client-invented scopes). */
  scopes: string[];
};

let memoryCredential: MemoryCredential | null = null;
let boundIdentityKey: string | null = null;
/** Bumped on every identity bind / full session wipe — mint must match. */
let sessionEpoch = 0;
const runBearerByRunId = new Map<string, string>();
const lastEventIdByRun = new Map<string, string>();

export function consultoriaIdentityKey(companyId: string, userId: string): string {
  return `${String(companyId)}:${String(userId)}`;
}

export function getConsultoriaSessionEpoch(): number {
  return sessionEpoch;
}

export function bumpConsultoriaSessionEpoch(): number {
  sessionEpoch += 1;
  return sessionEpoch;
}

export function getBoundConsultoriaIdentityKey(): string | null {
  return boundIdentityKey;
}

export function getConsultoriaMemoryCredential(): MemoryCredential | null {
  return memoryCredential;
}

export function setConsultoriaMemoryCredential(next: MemoryCredential | null): void {
  memoryCredential = next;
}

export function getConsultoriaScopes(): string[] {
  return memoryCredential?.scopes ? [...memoryCredential.scopes] : [];
}

export function hasConsultoriaScope(scope: string): boolean {
  return Boolean(memoryCredential?.scopes.includes(scope));
}

export function parseConsultoriaScopeString(scope: string | undefined | null): string[] {
  return String(scope || '')
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function getConsultoriaRunBearer(runId: string): string | undefined {
  return runBearerByRunId.get(runId);
}

export function setConsultoriaRunBearer(runId: string, token: string): void {
  runBearerByRunId.set(runId, token);
}

export function listConsultoriaActiveRunIds(): string[] {
  return Array.from(runBearerByRunId.keys());
}

/** Snapshot run_id → bearer for teardown cancels after maps are cleared. */
export function snapshotConsultoriaRunBearers(): Map<string, string> {
  return new Map(runBearerByRunId);
}

export function getConsultoriaLastEventId(runId: string): string | undefined {
  return lastEventIdByRun.get(runId);
}

export function setConsultoriaLastEventId(runId: string, eventId: string): void {
  lastEventIdByRun.set(runId, eventId);
}

export function consultoriaActiveRunCount(): number {
  return runBearerByRunId.size;
}

type RunTeardownHook = (bearers: Map<string, string>) => void;
let runTeardownHook: RunTeardownHook | null = null;

/** Register cancel-on-logout hook (wired by consultoriaClient). */
export function registerConsultoriaRunTeardown(hook: RunTeardownHook): void {
  runTeardownHook = hook;
}

/**
 * Bind the AI mint cache to the logged-in MES identity.
 * Switching company/user clears credential, run bearers and SSE cursors.
 */
export function bindConsultoriaIdentity(companyId: string, userId: string): void {
  const next = consultoriaIdentityKey(companyId, userId);
  if (boundIdentityKey && boundIdentityKey !== next) {
    const previousBearers = snapshotConsultoriaRunBearers();
    bumpConsultoriaSessionEpoch();
    memoryCredential = null;
    runBearerByRunId.clear();
    lastEventIdByRun.clear();
    runTeardownHook?.(previousBearers);
  }
  boundIdentityKey = next;
  if (memoryCredential && memoryCredential.identityKey !== next) {
    memoryCredential = null;
  }
}

export function clearConsultoriaCredential(): void {
  memoryCredential = null;
}

export function clearConsultoriaRun(runId?: string): void {
  if (runId) {
    runBearerByRunId.delete(runId);
    lastEventIdByRun.delete(runId);
    return;
  }
  runBearerByRunId.clear();
  lastEventIdByRun.clear();
}

/**
 * Full OPS session wipe (logout / identity loss).
 * Snapshots run bearers and invokes teardown hook so cancels can still fire.
 */
export function clearConsultoriaSession(): void {
  const bearers = snapshotConsultoriaRunBearers();
  bumpConsultoriaSessionEpoch();
  clearConsultoriaCredential();
  clearConsultoriaRun();
  boundIdentityKey = null;
  runTeardownHook?.(bearers);
}
